import React, { useMemo } from 'react';
import { Address } from '@hero/schema';
import { GoogleMapWithMarker, MapLocationData } from '../../../app/Map';
import { cn } from '@hero/web-lib';

export interface SavedLocationMapProps {
  /** Current location coordinates */
  coordinates?: { lat: number; lng: number };

  /** Optional address to display in search bar and card when we have coordinates (e.g. from saved location in edit mode) */
  initialAddress?: Pick<Address, 'fullAddress'> & Partial<Address>;

  /**
   * Live updates while the user is editing (marker drag, search, map click).
   * Use this to keep parent state (e.g. form coordinates) in sync. For "user confirmed location"
   * use onLocationEditEnd instead.
   */
  onLocationChange?: (locationInfo: MapLocationData) => void;
  /** Map height (default: '300px') */
  height?: string;
  /** Whether the map is in view-only mode (default: false = editable) */
  viewOnly?: boolean;
  /** Show search bar (map uses api-hooks internally) */
  showSearch?: boolean;
  /** Organization ID for client context */
  orgId?: string;
  /** Custom class name for the container */
  className?: string;
  /** Whether to show the address display below the map */
  showAddressDisplay?: boolean;
  /**
   * If true, automatically enter edit mode on the first provided location.
   * Use false when editing an existing saved location so user can save without touching the map.
   * @default true for new locations (no initialAddress), false when initialAddress is provided
   */
  enterEditModeInitially?: boolean;
  /** Callback when the user enters location edit mode (edit icon or auto-enter). Used to disable Save until done. */
  onLocationEditStart?: () => void;
  /** Callback when location edit ends (green check, cancel, or after save). Receives finalized location when user clicked done; used to re-enable Save and optionally update form. */
  onLocationEditEnd?: (location?: MapLocationData) => void;
}

/**
 * SavedLocationMap - Reusable component for displaying/editing a single location on a map
 *
 * Features:
 * - Single draggable marker
 * - Search functionality (optional)
 * - View-only or editable mode
 * - Address display below map (optional)
 *
 * @example
 * ```tsx
 * // Editable mode with search
 * <SavedLocationMap
 *   coordinates={{ lat: 24.7136, lng: 46.6753 }}
 *   onLocationChange={locationInfo => console.log(locationInfo.cords)}
 *   showSearch
 * />
 *
 * // View-only mode
 * <SavedLocationMap
 *   coordinates={{ lat: 24.7136, lng: 46.6753 }}
 *   address="Riyadh, Saudi Arabia"
 *   viewOnly
 * />
 * ```
 */
export function SavedLocationMap({
  coordinates,
  initialAddress,
  onLocationChange,
  height = '400px',
  viewOnly = false,
  showSearch = true,
  orgId,
  className,
  showAddressDisplay = true,
  enterEditModeInitially = !initialAddress,
  onLocationEditStart,
  onLocationEditEnd,
}: SavedLocationMapProps) {
  // Build initial locations array for the map; treat (0,0) as "no location yet" so we don't center on 0,0 (e.g. first open in edit mode before load).
  // When initialAddress is provided (e.g. edit mode), include it so the search bar and location card show address instead of raw coordinates.
  const mapLocations: MapLocationData[] = useMemo(() => {
    const valid = coordinates && (coordinates.lat !== 0 || coordinates.lng !== 0);
    if (valid) {
      return [
        {
          cords: coordinates,
          isAccurateLocation: true,
          fullAddress: initialAddress?.fullAddress,
          address: initialAddress as MapLocationData['address'],
          city: initialAddress?.city?.name,
        },
      ];
    }
    return [];
  }, [coordinates, initialAddress]);

  // Handle location change from map
  const handleMapLocationChange = (location: MapLocationData) => {
    onLocationChange?.(location);
  };

  const searchMode = showSearch ? 'google-only' : 'no-search';
  const mapMode = viewOnly ? 'view' : 'edit';

  const hasValidCoords = coordinates && (coordinates.lat !== 0 || coordinates.lng !== 0);

  // Only set an explicit center when we have valid coordinates.
  // When no coordinates are provided (e.g. creating a new location from Customer Drawer),
  // GoogleMapWithMarker falls back to: user's current location -> Riyadh default.
  const mapConfig = React.useMemo(
    () => ({
      height,
      showUserCurrentLocation: true,
      markerIcon: 'normal_location_icon' as const,
      ...(hasValidCoords ? { center: coordinates as google.maps.LatLngLiteral } : {}),
    }),
    [height, coordinates, hasValidCoords],
  );

  return (
    <div className={cn('relative isolate overflow-hidden rounded-lg border border-gray-200', className)}>
      <GoogleMapWithMarker
        searchMode={searchMode}
        locations={mapLocations}
        onLocationChange={handleMapLocationChange}
        onLocationEditStart={onLocationEditStart}
        onLocationEditEnd={onLocationEditEnd}
        locationsConfig={{
          countLimit: 1,
          mode: mapMode,
          enableAccurateLocationIndicator: false,
          enableAccurateLocationToggle: false,
          enableSavingPlaces: false,
          enterEditModeInitially,
        }}
        mapConfig={mapConfig}
        clientConfig={{
          orgId,
        }}
      />
    </div>
  );
}
